import { DatePicker } from '@liza/ui/components/atoms/DatePicker';
import { TimeField } from '@liza/ui/components/atoms/TimeField';
import { cn } from '@liza/ui/utils/cn';

export interface DateTimePickerProps {
  /** `yyyy-MM-dd'T'HH:mm` value, or `''` when empty. */
  value: string;
  /** Called with the new combined `yyyy-MM-dd'T'HH:mm` value, or `''` when cleared. */
  onChange: (value: string) => void;
  id?: string;
  onBlur?: () => void;
  required?: boolean;
  disabled?: boolean;
  /** Accessible label for the date field. The time field derives its label from it. */
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  className?: string;
  /** Applied to the date input; the time field gets `${data-testid}-time`. */
  'data-testid'?: string;
}

/**
 * Date-and-time picker composing {@link DatePicker} (calendar popover) with a
 * {@link TimeField}. Replaces the native `<input type="datetime-local">`. Splits
 * and recombines the `yyyy-MM-dd'T'HH:mm` value so each half edits independently
 * while preserving the other.
 */
export function DateTimePicker({
  value,
  onChange,
  id,
  onBlur,
  required,
  disabled,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  className,
  'data-testid': dataTestId,
}: DateTimePickerProps): React.ReactNode {
  const [date = '', time = ''] = value ? value.split('T') : [];

  const handleDateChange = (nextDate: string): void => {
    onChange(nextDate ? `${nextDate}T${time || '00:00'}` : '');
  };

  const handleTimeChange = (nextTime: string): void => {
    // A time without a date can't form a valid datetime, so ignore time edits until a
    // date is set. Call sites pre-populate a default datetime, so this isn't reachable today.
    if (!date) return;
    onChange(`${date}T${nextTime}`);
  };

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <DatePicker
        id={id}
        value={date}
        onChange={handleDateChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        data-testid={dataTestId}
        className="flex-1"
      />
      <TimeField
        value={time}
        onChange={handleTimeChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel ? `${ariaLabel} time` : 'Time'}
        aria-invalid={ariaInvalid}
        data-testid={dataTestId ? `${dataTestId}-time` : undefined}
        className="w-32"
      />
    </div>
  );
}
